import { useEffect, useRef } from 'react';

type EventHandler = (event: Event) => void;
type StopPropagationForWheel = () => void;

/**
 * @param timeout - number of milliseconds to handle the wheel event
 * @returns - a function that can be used to stop propagation for the wheel event for the given
 * number of milliseconds
 *
 * @remarks
 * There's an ongoing GitHub issue about Chrome and wheel events. This hook was inspired by a post
 * in that issue: https://github.com/facebook/react/issues/14856#issuecomment-829318408
 */
export const useWheelEvent = (
    timeout = 300,
    eventHandler: EventHandler,
): StopPropagationForWheel => {
    const wheelTimeout = useRef<number | boolean>(false);

    // handle wheel event while wheelTimeout is set
    useEffect(() => {
        const maybeHandleEvent = (e: Event): void => {
            if (typeof wheelTimeout.current === 'number') {
                eventHandler(e);
            }
        };

        document.body.addEventListener('wheel', maybeHandleEvent, {
            passive: false,
        });

        return () =>
            document.body.removeEventListener('wheel', maybeHandleEvent);
    }, []);

    // return a function that can be used to handle wheel events for timeout ms
    return () => {
        if (typeof wheelTimeout.current === 'number') {
            window.clearTimeout(wheelTimeout.current);
        }

        wheelTimeout.current = window.setTimeout(() => {
            wheelTimeout.current = false;
        }, timeout);
    };
};
